<template>
    <div
        class="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8"
    >
        <div class="max-w-md w-full space-y-8">
            <!-- Header -->
            <div class="text-center">
                <div
                    class="mx-auto h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center"
                >
                    <span class="text-white font-bold text-xl">LMS</span>
                </div>
                <h2
                    class="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white"
                >
                    Create your account
                </h2>
                <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Join our Learning Management System
                </p>
            </div>

            <!-- Registration Form -->
            <form class="mt-8 space-y-6" @submit.prevent="handleRegister">
                <div class="space-y-4">
                    <!-- Name Field -->
                    <div>
                        <label
                            for="name"
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            Full Name
                        </label>
                        <div class="mt-1 relative">
                            <input
                                id="name"
                                v-model="form.name"
                                type="text"
                                required
                                :class="[
                                    'appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm',
                                    errors.name
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                                        : 'border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white',
                                ]"
                                placeholder="Enter your full name"
                            />
                            <div
                                v-if="errors.name"
                                class="mt-1 text-sm text-red-600"
                            >
                                {{ errors.name }}
                            </div>
                        </div>
                    </div>

                    <!-- Email Field -->
                    <div>
                        <label
                            for="email"
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            Email Address
                        </label>
                        <div class="mt-1 relative">
                            <input
                                id="email"
                                v-model="form.email"
                                type="email"
                                required
                                :class="[
                                    'appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm',
                                    errors.email
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                                        : 'border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white',
                                ]"
                                placeholder="Enter your email"
                            />
                            <div
                                v-if="errors.email"
                                class="mt-1 text-sm text-red-600"
                            >
                                {{ errors.email }}
                            </div>
                        </div>
                    </div>

                    <!-- Password Field -->
                    <div>
                        <label
                            for="password"
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            Password
                        </label>
                        <div class="mt-1 relative">
                            <input
                                id="password"
                                v-model="form.password"
                                :type="showPassword ? 'text' : 'password'"
                                required
                                :class="[
                                    'appearance-none block w-full px-3 py-2 pr-10 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm',
                                    errors.password
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                                        : 'border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white',
                                ]"
                                placeholder="Create a password"
                            />
                            <button
                                type="button"
                                @click="showPassword = !showPassword"
                                class="absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                                <EyeIcon
                                    v-if="!showPassword"
                                    class="h-4 w-4 text-gray-400"
                                />
                                <EyeSlashIcon
                                    v-else
                                    class="h-4 w-4 text-gray-400"
                                />
                            </button>
                            <div
                                v-if="errors.password"
                                class="mt-1 text-sm text-red-600"
                            >
                                {{ errors.password }}
                            </div>
                        </div>
                    </div>

                    <!-- Confirm Password Field -->
                    <div>
                        <label
                            for="password_confirmation"
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            Confirm Password
                        </label>
                        <div class="mt-1 relative">
                            <input
                                id="password_confirmation"
                                v-model="form.password_confirmation"
                                :type="
                                    showConfirmPassword ? 'text' : 'password'
                                "
                                required
                                :class="[
                                    'appearance-none block w-full px-3 py-2 pr-10 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm',
                                    errors.password_confirmation
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                                        : 'border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white',
                                ]"
                                placeholder="Confirm your password"
                            />
                            <button
                                type="button"
                                @click="
                                    showConfirmPassword = !showConfirmPassword
                                "
                                class="absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                                <EyeIcon
                                    v-if="!showConfirmPassword"
                                    class="h-4 w-4 text-gray-400"
                                />
                                <EyeSlashIcon
                                    v-else
                                    class="h-4 w-4 text-gray-400"
                                />
                            </button>
                            <div
                                v-if="errors.password_confirmation"
                                class="mt-1 text-sm text-red-600"
                            >
                                {{ errors.password_confirmation }}
                            </div>
                        </div>
                    </div>

                    <!-- Role Selection -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            I am a
                        </label>
                        <div class="mt-1 space-y-2">
                            <label class="flex items-center">
                                <input
                                    v-model="form.role"
                                    type="radio"
                                    value="student"
                                    class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                />
                                <span
                                    class="ml-2 text-sm text-gray-700 dark:text-gray-300"
                                    >Student</span
                                >
                            </label>
                            <label class="flex items-center">
                                <input
                                    v-model="form.role"
                                    type="radio"
                                    value="teacher"
                                    class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                />
                                <span
                                    class="ml-2 text-sm text-gray-700 dark:text-gray-300"
                                    >Teacher</span
                                >
                            </label>
                        </div>
                        <div
                            v-if="errors.role"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.role }}
                        </div>
                    </div>
                </div>

                <!-- Terms and Conditions -->
                <div class="flex items-center">
                    <input
                        id="terms"
                        v-model="form.terms"
                        type="checkbox"
                        required
                        class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label
                        for="terms"
                        class="ml-2 block text-sm text-gray-900 dark:text-gray-300"
                    >
                        I agree to the
                        <a
                            href="#"
                            class="text-primary-600 hover:text-primary-500"
                            >Terms and Conditions</a
                        >
                        and
                        <a
                            href="#"
                            class="text-primary-600 hover:text-primary-500"
                            >Privacy Policy</a
                        >
                    </label>
                </div>
                <div v-if="errors.terms" class="mt-1 text-sm text-red-600">
                    {{ errors.terms }}
                </div>

                <!-- Submit Button -->
                <div>
                    <button
                        type="submit"
                        :disabled="loading"
                        class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <span
                            v-if="loading"
                            class="absolute left-0 inset-y-0 flex items-center pl-3"
                        >
                            <svg
                                class="animate-spin h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    class="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    stroke-width="4"
                                ></circle>
                                <path
                                    class="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                        </span>
                        {{ loading ? "Creating account..." : "Create account" }}
                    </button>
                </div>

                <!-- Login Link -->
                <div class="text-center">
                    <span class="text-sm text-gray-600 dark:text-gray-400">
                        Already have an account?
                        <router-link
                            to="/login"
                            class="font-medium text-primary-600 hover:text-primary-500"
                        >
                            Sign in
                        </router-link>
                    </span>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, reactive } from "vue";
import { useRouter } from "vue-router";
import { EyeIcon, EyeSlashIcon } from "@heroicons/vue/24/outline";

const router = useRouter();

// Form state
const loading = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

const form = reactive({
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
    role: "student",
    terms: false,
});

// Validation errors
const errors = reactive<Record<string, string>>({});

// Validate form
const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!form.name.trim()) {
        newErrors.name = "Full name is required";
    } else if (form.name.length < 2) {
        newErrors.name = "Name must be at least 2 characters";
    }

    // Email validation
    if (!form.email.trim()) {
        newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    if (!form.password) {
        newErrors.password = "Password is required";
    } else if (form.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(form.password)) {
        newErrors.password =
            "Password must contain at least one uppercase letter, one lowercase letter, and one number";
    }

    // Password confirmation
    if (!form.password_confirmation) {
        newErrors.password_confirmation = "Please confirm your password";
    } else if (form.password !== form.password_confirmation) {
        newErrors.password_confirmation = "Passwords do not match";
    }

    // Role validation
    if (!form.role) {
        newErrors.role = "Please select a role";
    }

    // Terms validation
    if (!form.terms) {
        newErrors.terms = "You must agree to the terms and conditions";
    }

    // Clear previous errors and set new ones
    Object.keys(errors).forEach((key) => delete errors[key]);
    Object.assign(errors, newErrors);

    return Object.keys(newErrors).length === 0;
};

// Handle registration
const handleRegister = async () => {
    if (!validateForm()) {
        return;
    }

    loading.value = true;

    try {
        // Call actual API
        const response = await fetch("/api/v1/register", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({
                name: form.name,
                email: form.email,
                password: form.password,
                password_confirmation: form.password_confirmation,
                role: form.role,
                terms: form.terms,
            }),
        });

        const data = await response.json();

        if (data.success) {
            // Show success message
            alert(
                data.message ||
                    "Registration successful! You can now log in with your credentials.",
            );

            // Redirect to login
            router.push("/login");
        } else {
            // Handle API errors
            if (data.errors) {
                for (const [key, messages] of Object.entries(data.errors)) {
                    (errors as any)[key] = Array.isArray(messages)
                        ? (messages as string[])[0]
                        : String(messages);
                }
            } else {
                errors.email =
                    data.message || "Registration failed. Please try again.";
            }
        }
    } catch (error) {
        console.error("Registration failed:", error);
        errors.email = "Network error. Please try again.";
    } finally {
        loading.value = false;
    }
};
</script>
